import { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Wallet,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Receipt,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFinance } from '@/contexts/FinanceContext';
import { AccountModal } from './AccountModal';
import { CardInvoiceCloser } from '@/features/credit-cards/CardInvoiceCloser';
import type { FinanceAccount, AccountType } from '@/types/finance';
import { useCurrentClient } from '@/hooks/useCurrentClient';

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  checking: 'Conta Corrente',
  savings: 'Poupança',
  wallet: 'Carteira',
  investment: 'Investimento',
  credit_card: 'Cartão de Crédito',
  other: 'Outro',
};

const fmt = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

export function AccountsPage() {
  const { clientId } = useCurrentClient();
  const { accounts, deleteAccount, isLoading } = useFinance();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<FinanceAccount | undefined>();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [closeInvoiceFor, setCloseInvoiceFor] = useState<FinanceAccount | undefined>();

  if (!clientId) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Selecione um cliente para gerenciar as contas bancárias.</p>
      </div>
    );
  }

  const totalBalance = accounts.filter((a) => a.isActive).reduce((s, a) => s + a.currentBalance, 0);
  const activeAccounts = accounts.filter((a) => a.isActive);

  const openCreate = () => {
    setEditingAccount(undefined);
    setModalOpen(true);
  };
  const openEdit = (a: FinanceAccount) => {
    setEditingAccount(a);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Contas</h1>
          <p className="text-sm text-muted-foreground">
            {activeAccounts.length} conta{activeAccounts.length !== 1 ? 's' : ''} ativa
            {activeAccounts.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Nova Conta
        </Button>
      </div>

      {/* Saldo total */}
      <Card className="mb-6 border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              <Wallet className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Saldo Total em Contas</p>
              <p
                className={`text-2xl font-bold ${totalBalance >= 0 ? 'text-income' : 'text-expense'}`}
              >
                {fmt(totalBalance)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl border bg-muted" />
          ))}
        </div>
      ) : activeAccounts.length === 0 ? (
        <div className="rounded-xl border border-border bg-white p-12 text-center text-muted-foreground">
          <Wallet className="mx-auto mb-3 h-12 w-12 opacity-30" />
          <p className="font-medium">Nenhuma conta cadastrada</p>
          <p className="mt-1 text-sm">Crie a primeira conta bancária para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {activeAccounts.map((a) => (
            <Card key={a.id} className="relative overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full w-1"
                style={{ backgroundColor: a.color }}
              />
              <CardHeader className="pb-2 pl-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    {a.type === 'credit_card' && (
                      <CreditCard className="mt-1 h-4 w-4 text-primary" />
                    )}
                    <div>
                      <CardTitle className="text-base font-semibold">{a.name}</CardTitle>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {ACCOUNT_TYPE_LABELS[a.type]}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {a.type === 'credit_card' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary hover:text-primary"
                        onClick={() => setCloseInvoiceFor(a)}
                        title="Fechar fatura"
                      >
                        <Receipt size={14} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEdit(a)}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeletingId(a.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pl-5 pt-0">
                {a.type === 'credit_card' ? (
                  <>
                    <p
                      className={`text-2xl font-bold ${a.currentBalance >= 0 ? 'text-income' : 'text-expense'}`}
                    >
                      {fmt(a.currentBalance)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Saldo da fatura — negativo significa em aberto
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      {a.closingDay && <span>Fecha dia {a.closingDay}</span>}
                      {a.dueDay && <span>Vence dia {a.dueDay}</span>}
                      {a.creditLimit !== undefined && <span>Limite {fmt(a.creditLimit)}</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <p
                      className={`text-2xl font-bold ${a.currentBalance >= 0 ? 'text-income' : 'text-expense'}`}
                    >
                      {fmt(a.currentBalance)}
                    </p>
                    <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3 text-income" />
                        <span>Inicial: {fmt(a.initialBalance)}</span>
                      </div>
                      {a.currentBalance !== a.initialBalance && (
                        <div className="flex items-center gap-1">
                          {a.currentBalance - a.initialBalance >= 0 ? (
                            <TrendingUp className="h-3 w-3 text-income" />
                          ) : (
                            <TrendingDown className="h-3 w-3 text-expense" />
                          )}
                          <span
                            className={
                              a.currentBalance - a.initialBalance >= 0
                                ? 'text-income'
                                : 'text-expense'
                            }
                          >
                            {a.currentBalance - a.initialBalance >= 0 ? '+' : ''}
                            {fmt(a.currentBalance - a.initialBalance)}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AccountModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        {...(editingAccount ? { editAccount: editingAccount } : {})}
      />

      {closeInvoiceFor && (
        <CardInvoiceCloser
          open={!!closeInvoiceFor}
          onOpenChange={(o) => !o && setCloseInvoiceFor(undefined)}
          card={closeInvoiceFor}
        />
      )}

      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. As transações vinculadas serão afetadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingId) {
                  deleteAccount(deletingId);
                  setDeletingId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
